import { useState } from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { Caption } from './primitives';

/**
 * XpExplainer — barème XP collapsible pour Mission Control (MVP2.5 T7, bug A8).
 *
 * Spec : `docs/WAKEUP_MVP2.5.md §2 T7`. Léon n'a aucun retour visuel pour
 * comprendre POURQUOI il vient de gagner 25 pts ou 2 pts. On ajoute en bas
 * de Mission Control un bouton collapsible qui détaille le barème complet
 * (`docs/XP_BAREME.md`).
 *
 * Comportement :
 * - Fermé par défaut (n'occupe pas l'espace visuel critique du cockpit).
 * - Clic = toggle. Le contenu déplié reste compact.
 * - Lien direct vers `docs/XP_BAREME.md` pour les détails (« pour les
 *   curieux »).
 *
 * Anti-cibles (PRODUCT §5.4) : pas de gamification théâtrale, pas de
 * badges, pas de niveaux. Juste un mémo barème factuel.
 */

interface XpRow {
  label: string;
  xp: number;
  // Note pédagogique courte : pourquoi cette valeur (inverse experience —
  // plus l'exercice est long et profond, plus l'XP est élevé).
  note: string;
}

const BAREME: XpRow[] = [
  { label: 'Carte d\'ouverture revue (Mission Control)', xp: 25, note: 'session matin' },
  { label: 'Coup d\'ouverture trouvé (Mémoire / Riposte / ...)', xp: 3, note: 'court · théorie' },
  { label: 'Puzzle tactique résolu', xp: 2, note: 'court · répétitif' },
  { label: 'Coup correct en finale', xp: 15, note: 'long · technique' },
  { label: 'Autopsie complète d\'une partie', xp: 30, note: 'long · ~90s analyse' },
];

export function XpExplainer({ className = '' }: { className?: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid="xp-explainer"
      className={`rounded-xl border border-ink-700 bg-ink-800/40 ${className}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="xp-explainer-content"
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-ink-800/60 transition-all duration-150 rounded-xl"
      >
        <span className="flex items-center gap-2">
          <Caption color="acid">Comment je gagne des points ?</Caption>
        </span>
        <span aria-hidden="true" className="text-ink-300">
          {open ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </span>
      </button>
      {open && (
        <div id="xp-explainer-content" className="px-4 pb-4 pt-1 flex flex-col gap-3">
          <p className="text-body-sm text-paper-50 leading-relaxed">
            Plus l'exercice est <strong>long et profond</strong>, plus tu gagnes
            de points. Récompense la concentration, pas la répétition mécanique.
          </p>
          <ul className="flex flex-col gap-1.5">
            {BAREME.map((row) => (
              <li
                key={row.label}
                className="flex items-baseline justify-between gap-3 font-mono text-xs"
              >
                <span className="text-paper-50">{row.label}</span>
                <span className="flex items-baseline gap-2 whitespace-nowrap">
                  <span className="text-acid-500 font-semibold tabular-nums">+{row.xp} pts</span>
                  <span className="text-ink-300 italic">{row.note}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-ink-300 italic">
            Objectif quotidien : 100 pts (~30 min équilibrées). Pas de streak
            qui se casse, pas de boutique, pas de notification. Les points
            sont juste un repère de progression.
          </p>
        </div>
      )}
    </section>
  );
}
